import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {window} from 'ssr-window';
import uuidV4 from 'uuid/v4';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import EventListItem from 'event/EventListItem';
import QuickFilters from 'filters/QuickFilters';
import {toggleInfoPanelView} from '../search-actions';
import WithSearchControls from '../controls/WithSearchControls';
import SearchTermToggle from '../controls/SearchTermToggle';
import SearchInput from '../controls/SearchInput';
import SearchDateTimePickers from '../controls/SearchDateTimePickers';
import SearchActions from '../controls/SearchActions';
import SpotListHeading from './SpotListHeading';
import SpotListBreadcrumbs from './SpotListBreadcrumbs';

class SpotListSearchControls extends Component {
    static propTypes = {
        isMobile: PropTypes.bool,
        isAdmin: PropTypes.bool.isRequired,
        timezone: PropTypes.string.isRequired,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        searchString: PropTypes.string,
        searchBounds: PropTypes.shape({
            swLat: PropTypes.number,
            swLng: PropTypes.number,
            neLat: PropTypes.number,
            neLng: PropTypes.number,
        }),
        isCanadaEnabled: PropTypes.bool,
        isSearching: PropTypes.bool,
        isValidStart: PropTypes.bool,
        hideInfoToggle: PropTypes.bool,
        onInputChange: PropTypes.func.isRequired,
        onDateTimeChange: PropTypes.func,
        onSubmit: PropTypes.func.isRequired,
        onSearchClick: PropTypes.func,
        city: PropTypes.object.isRequired,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        event: PropTypes.object,
        numSpots: PropTypes.number.isRequired,
        searchType: PropTypes.string.isRequired,
        breadcrumbs: PropTypes.object,
        showNoSpotsFound: PropTypes.bool,
        onToggleInfoPanel: PropTypes.func.isRequired,
        onToggleInfoModal: PropTypes.func,
        onToggleEventList: PropTypes.func,
    };
    state = {
        inputKey: uuidV4(),
        dateTimeKey: uuidV4(),
        showActions: false,
    };

    componentDidMount() {
        if (window.innerWidth < 900) {
            this.props.onToggleInfoPanel(false);
        }
    }

    _onActionsToggle = isShowing => {
        this.setState({
            showActions: isShowing,
        });
    };

    _onShowInfoClick = () => {
        const {isMobile, onToggleInfoPanel, onToggleInfoModal} = this.props;

        if (isMobile) {
            onToggleInfoModal();
        } else {
            onToggleInfoPanel(true);
        }
    };

    _onSuggestSelect = () => {
        this.setState({
            showActions: true,
        });
    };

    _onDateTimeChange = data => {
        this.setState({
            showActions: true,
        });

        this.props.onDateTimeChange(data);
    };

    _onSearchClick = () => {
        this.setState({
            showActions: false,
        });

        this.props.onSearchClick();
    };

    _onCancelClick = evt => {
        this.setState({
            inputKey: uuidV4(),
            dateTimeKey: uuidV4(),
            showActions: false,
        });
    };

    render() {
        const {
            isMobile,
            isAdmin,
            timezone,
            isMonthly,
            searchString,
            starts,
            ends,
            searchBounds,
            isSearching,
            isValidStart,
            hideInfoToggle,
            isCanadaEnabled,
            onInputChange,
            onSubmit,
            searchType,
            breadcrumbs,
            city,
            destination,
            event,
            showNoSpotsFound,
            numSpots,
            onToggleEventList,
        } = this.props;
        const {inputKey, dateTimeKey, showActions} = this.state;

        return (
            <div className="SpotListSearchControls">
                {(!isMobile || (isMobile && searchType !== 'event')) && (
                    <div className="SpotListSearchControls-info">
                        {searchType !== 'event' && (
                            <SearchTermToggle isSearching={isSearching} />
                        )}
                        {!isMobile && searchType !== 'city' && (
                            <SpotListBreadcrumbs data={breadcrumbs} />
                        )}
                    </div>
                )}
                {searchType !== 'address' && searchType !== 'event' && (
                    <SpotListHeading
                        showEventsLink={!isMobile}
                        hideInfoToggle={hideInfoToggle}
                        onShowInfoClick={this._onShowInfoClick}
                        onToggleEventList={onToggleEventList}
                    />
                )}
                {searchType === 'event' && (
                    <EventListItem
                        city={city}
                        destination={destination}
                        event={event}
                        isMobile={isMobile}
                        showFindParking={false}
                        showVenue
                        showYear
                        onShowInfoClick={this._onShowInfoClick}
                    />
                )}
                <div className="SpotListSearchControls-controls">
                    {searchType !== 'event' && (
                        <SearchInput
                            key={inputKey}
                            isMonthly={isMonthly}
                            isSearching={isSearching}
                            searchString={searchString}
                            searchBounds={searchBounds}
                            isCanadaEnabled={isCanadaEnabled}
                            onSuggestSelect={this._onSuggestSelect}
                            onChange={onInputChange}
                            onSubmit={onSubmit}
                        />
                    )}
                    <SearchDateTimePickers
                        key={dateTimeKey}
                        isAdmin={isAdmin}
                        timezone={timezone}
                        isMonthly={isMonthly}
                        starts={starts}
                        ends={ends}
                        onChange={this._onDateTimeChange}
                    />
                    {showActions && (
                        <SearchActions
                            addKeyListeners={!isMobile}
                            showCancel
                            searchButtonText="Update Search"
                            isSearching={isSearching}
                            isValidStart={isValidStart}
                            onSearchClick={this._onSearchClick}
                            onCancelClick={this._onCancelClick}
                        />
                    )}
                </div>
                {numSpots > 0 && !showNoSpotsFound && !showActions && (
                    <QuickFilters
                        showReservationTypes={isMonthly}
                        visibleSlugs={['self-park', 'touchless']}
                        allowTouchlessTooltip
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = ({
    city: {data: city},
    destination: {data: destination},
    event: {data: event},
    filters: {numActive},
    search: {
        data: {breadcrumbs, searchType, showNoSpotsFound},
    },
}) => {
    return {
        city,
        destination,
        event,
        breadcrumbs,
        numActiveFilters: numActive,
        searchType,
        showNoSpotsFound,
    };
};

const mapDispatchToProps = {
    onToggleInfoPanel: toggleInfoPanelView,
};

const enhance = compose(
    WithSearchControls,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SpotListSearchControls);
